import {
  CoverShortcutTile,
  Page,
  PageListItem,
  PageSearchResultItem,
} from "../../api/types/page.js";
import type { ImageContent } from "@modelcontextprotocol/sdk/types.js";
import { createHash } from "crypto";
import { fileTypeFromBuffer } from "file-type";

const MARKDOWN_IMAGE_REGEX = /!\[([^\]]*)\]\(([^)]+)\)/g;
const PAGE_IMAGE_RESOURCE_URI_REGEX =
  /^zeroheight:\/\/image\/([1-9]\d*)\/([a-f0-9]{64})$/i;
const PAGE_ATTACHMENT_RESOURCE_URI_REGEX =
  /^zeroheight:\/\/attachment\/([1-9]\d*)\/([a-f0-9]{64})$/i;

const ANNOTATIONS_BLOCK_REGEX = /<annotations\b[^>]*>/i;

// Matches attachment lines emitted by the Rails prosemirror_to_markdown adapter:
//   - [name](url) | size | mimeType | date
const MARKDOWN_ATTACHMENT_REGEX =
  /^- \[([^\]]+)\]\(([^)]+)\) \| ([^|]+) \| ([^|]+) \| ([^\n|]+)$/gm;

export interface PageAttachment {
  name: string;
  url: string;
  size: string;
  type: string;
  date: string;
}

const MIME_TYPE_BY_EXTENSION: Record<string, string> = {
  png: "image/png",
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  gif: "image/gif",
  webp: "image/webp",
  svg: "image/svg+xml",
  avif: "image/avif",
  bmp: "image/bmp",
  ico: "image/x-icon",
  tif: "image/tiff",
  tiff: "image/tiff",
  txt: "text/plain",
  csv: "text/csv",
  json: "application/json",
  xml: "application/xml",
};

const GENERIC_CONTENT_TYPES: ReadonlySet<string> = new Set([
  "application/octet-stream",
  "binary/octet-stream",
  "application/x-octet-stream",
  "*/*",
  "",
]);

export function formatPageAsMarkdown(page: Page): string {
  const content =
    page.content ||
    page.tabs?.map((tab) => `# ${tab.name}\n${tab.content}\n---\n`).join("\n");
  const shortcutTiles = formatCoverShortcutTiles(page.cover_shortcut_tiles);
  const frontmatter = [
    `---`,
    `title: ${page.name}`,
    `url: ${page.url}`,
    page.introduction ? `introduction: ${page.introduction}` : undefined,
    `---`,
  ]
    .filter((part) => part != null)
    .join("\n");

  return shortcutTiles
    ? [frontmatter, "", shortcutTiles, "", content].join("\n")
    : [frontmatter, "", content].join("\n");
}

export function formatPageListItemsAsMarkdown(
  pages: (PageListItem | PageSearchResultItem)[],
): string {
  return pages
    .map((page) => `- ${page.name ?? "Untitled page"} (ID: ${page.id})`)
    .join("\n");
}

export async function fetchBlobFromUrl(
  resourceUrl: string,
): Promise<{ blob: string; mimeType: string } | null> {
  // Presigned S3 URLs embedded in page content may be double-encoded
  // (e.g. %252F instead of %2F). Normalise one level of %25-encoding so the
  // URL reaches S3 with the single-encoded query params it expects.
  const fetchUrl = resourceUrl.replace(/%25([0-9A-Fa-f]{2})/gi, "%$1");
  try {
    const response = await fetch(fetchUrl);
    if (!response.ok) {
      return null;
    }
    const buffer = Buffer.from(await response.arrayBuffer());
    const mimeType = await getMimeType(
      fetchUrl,
      response.headers.get("content-type"),
      buffer,
    );
    return { blob: buffer.toString("base64"), mimeType };
  } catch {
    return null;
  }
}

export async function extractImageUrlsFromPage(
  content: string,
): Promise<ImageContent[]> {
  const imageUrls = extractImageUrls(content);

  const embeddedResources: Array<ImageContent | null> = await Promise.all(
    imageUrls.map(async (uri) => {
      if (!isHttpUrl(uri)) {
        return null;
      }

      const image = await fetchBlobFromUrl(uri);
      if (!image) {
        return null;
      }

      return {
        type: "image",
        data: image.blob,
        mimeType: image.mimeType,
        _meta: {
          uri,
        },
      } satisfies ImageContent;
    }),
  );

  return embeddedResources.filter((r) => r != null);
}

export function replacePageImageReferencesWithResourceUris(
  pageId: number,
  content: string,
): string {
  return content.replace(MARKDOWN_IMAGE_REGEX, (match, altText, target) => {
    const url = parseMarkdownImageTarget(target);
    if (!url || !isHttpUrl(url)) return match;

    return `![${altText}](${getPageImageResourceUri(pageId, url)})`;
  });
}

export function findPageImageUrlByResourceHash(
  content: string,
  imageHash: string,
): string | null {
  for (const match of content.matchAll(MARKDOWN_IMAGE_REGEX)) {
    const url = parseMarkdownImageTarget(match[2]);
    if (!url || !isHttpUrl(url)) continue;

    if (hashPageResourceUrl(url) === imageHash) {
      return url;
    }
  }

  return null;
}

export function getPageImageResourceUri(pageId: number, url: string): string {
  return `zeroheight://image/${pageId}/${hashPageResourceUrl(url)}`;
}

export function parsePageImageResourceUri(
  uri: string,
): { pageId: number; imageHash: string } | null {
  const match = PAGE_IMAGE_RESOURCE_URI_REGEX.exec(uri.trim());
  if (!match) return null;

  return {
    pageId: Number(match[1]),
    imageHash: match[2].toLowerCase(),
  };
}

export function pageHasAnnotations(content: string): boolean {
  return ANNOTATIONS_BLOCK_REGEX.test(content);
}

export function extractPageAttachments(content: string): PageAttachment[] {
  const attachments: PageAttachment[] = [];
  for (const match of content.matchAll(MARKDOWN_ATTACHMENT_REGEX)) {
    const url = match[2].trim();
    if (!isHttpUrl(url)) continue;
    attachments.push({
      name: match[1].trim(),
      url,
      size: match[3].trim(),
      type: match[4].trim(),
      date: match[5].trim(),
    });
  }
  return attachments;
}

export function replacePageAttachmentReferencesWithResourceUris(
  pageId: number,
  content: string,
): string {
  return content.replace(
    MARKDOWN_ATTACHMENT_REGEX,
    (match, name, url, size, type, date) => {
      const trimmedUrl = url.trim();
      if (!isHttpUrl(trimmedUrl)) return match;

      return `- [${name}](${getPageAttachmentResourceUri(
        pageId,
        trimmedUrl,
      )}) | ${size} | ${type} | ${date}`;
    },
  );
}

export function findPageAttachmentByResourceHash(
  content: string,
  attachmentHash: string,
): PageAttachment | null {
  for (const attachment of extractPageAttachments(content)) {
    if (hashPageResourceUrl(attachment.url) === attachmentHash) {
      return attachment;
    }
  }

  return null;
}

export function getPageAttachmentResourceUri(
  pageId: number,
  url: string,
): string {
  return `zeroheight://attachment/${pageId}/${hashPageResourceUrl(url)}`;
}

export function parsePageAttachmentResourceUri(
  uri: string,
): { pageId: number; attachmentHash: string } | null {
  const match = PAGE_ATTACHMENT_RESOURCE_URI_REGEX.exec(uri.trim());
  if (!match) return null;

  return {
    pageId: Number(match[1]),
    attachmentHash: match[2].toLowerCase(),
  };
}

function extractImageUrls(content: string): string[] {
  const fromMarkdownImages = Array.from(content.matchAll(MARKDOWN_IMAGE_REGEX))
    .map((match) => parseMarkdownImageTarget(match[2]))
    .filter((url): url is string => !!url);

  const fromImageAttachments = extractPageAttachments(content)
    .filter((attachment) => attachment.type.startsWith("image/"))
    .map((attachment) => attachment.url);

  return [...fromMarkdownImages, ...fromImageAttachments].filter(
    (url, index, self) => self.indexOf(url) === index,
  );
}

function hashPageResourceUrl(url: string): string {
  return createHash("sha256")
    .update(stripQueryParameters(url), "utf8")
    .digest("hex")
    .toLowerCase();
}

function stripQueryParameters(url: string): string {
  const parsedUrl = new URL(url);
  parsedUrl.search = "";
  return parsedUrl.toString();
}

function parseMarkdownImageTarget(target: string): string | null {
  const trimmedTarget = target.trim();
  if (trimmedTarget.length === 0) {
    return null;
  }

  if (trimmedTarget.startsWith("<")) {
    const closingBracketIndex = trimmedTarget.indexOf(">", 1);
    if (closingBracketIndex > 1) {
      return trimmedTarget.slice(1, closingBracketIndex).trim();
    }
  }

  const firstWhitespaceIndex = trimmedTarget.search(/\s/);
  const url =
    firstWhitespaceIndex === -1
      ? trimmedTarget
      : trimmedTarget.slice(0, firstWhitespaceIndex);

  return stripWrappingQuotes(url.trim());
}

function stripWrappingQuotes(value: string): string {
  if (
    (value.startsWith('"') && value.endsWith('"')) ||
    (value.startsWith("'") && value.endsWith("'"))
  ) {
    return value.slice(1, -1);
  }

  return value;
}

function formatCoverShortcutTiles(
  tiles?: CoverShortcutTile[],
): string | undefined {
  if (!tiles || tiles.length === 0) {
    return undefined;
  }

  return [
    `<shortcut_tiles type="cover">`,
    tiles.map(formatCoverShortcutTile).join("\n"),
    `</shortcut_tiles>`,
  ].join("\n");
}

function formatCoverShortcutTile(tile: CoverShortcutTile): string {
  const attributes = [`url="${escapeAttribute(tile.url)}"`];

  if (tile.page_id != null) {
    attributes.push(`page-id="${tile.page_id}"`);
  }

  const lines = [`<shortcut_tile ${attributes.join(" ")}>`];

  if (tile.image_url) {
    lines.push(`![${escapeMarkdownText(tile.title)}](${tile.image_url})`, "");
  }

  if (tile.title) {
    lines.push(`**${escapeMarkdownText(tile.title)}**`, "");
  }

  if (tile.description) {
    lines.push(tile.description);
  }

  lines.push(`</shortcut_tile>`);

  return lines.join("\n");
}

function escapeAttribute(value: string): string {
  return value.replace(/"/g, "&quot;");
}

function escapeMarkdownText(value: string): string {
  return value.replace(/([\\[\]()])/g, "\\$1");
}

function isHttpUrl(value: string): boolean {
  try {
    const url = new URL(value);
    return url.protocol === "http:" || url.protocol === "https:";
  } catch {
    return false;
  }
}

async function getMimeType(
  uri: string,
  contentTypeHeader: string | null,
  buffer: Buffer,
): Promise<string> {
  const normalizedContentType = contentTypeHeader
    ?.split(";")[0]
    ?.trim()
    .toLowerCase();
  if (
    normalizedContentType &&
    !GENERIC_CONTENT_TYPES.has(normalizedContentType)
  ) {
    return normalizedContentType;
  }

  const detectedType = await fileTypeFromBuffer(buffer);
  if (detectedType) {
    return detectedType.mime;
  }

  try {
    const pathname = new URL(uri).pathname;
    const extension = pathname.split(".").pop()?.toLowerCase();
    if (extension && MIME_TYPE_BY_EXTENSION[extension]) {
      return MIME_TYPE_BY_EXTENSION[extension];
    }
  } catch {
    // noop
  }

  return "application/octet-stream";
}
